/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, Pressable, Image } from '@components/atoms'
// 로고 규칙: 원형(라이언) 단독 또는 원형+텍스트 조합만 쓴다. 텍스트 로고 단독 사용 금지.
// 조합은 가로(원형 좌·텍스트 우, 상하 중앙) 또는 세로(원형 위·텍스트 아래, 좌우 중앙). 여기는 가로.
import logoPng from '@public/images/logo/logo_black.png'
import logoWordingPng from '@public/images/logo/logo_wording.png'
import { CookieConsent, DisplayCurrency } from '@utils'
import { useDisplayCurrency } from '../monolith/useDisplayCurrency'
import { FORUM_URL, forumEntryUrl } from '@configs'
import { SEO_KO } from '@src/configs/seo.config'

import { useNavigate } from 'react-router-dom'
import { mono, monoFont, monoGutter, monoMaxWidth } from '../monolith/monolith.tokens'

/**
 * 사이트 공용 푸터 (2026-09-08 Monolith 디자인으로 교체 — 이전 구현은 Footer.legacy.tsx).
 * 홈·Shop·제품·Contact 등 <Footer /> 를 쓰는 모든 페이지가 이 하나를 공유한다. 여기만 고치면 전부 바뀐다.
 *
 * 2026-09-09 사이트맵 §9: Products / Shop / Support / Company 4열. 푸터는 네비의 거울이라 GNB 와 중복돼도 된다.
 * 시즌 카피 시그니처("the way, the truth, and the life")는 사이트 전체에서 두 자리만 허용되고,
 * 그중 한 자리가 여기다 — 워드마크 바로 아래 소문자 전체형. (HOMEPAGE_MOCKUP_PLAN §2)
 */
export const Footer = () => {
	// hook
	const navigate = useNavigate()
	/*
		표시 통화 스위치 (PRICING §6, 2026-09-16). 기본값은 방문 국가로 판정한다 — 한국이면 원화, 아니면
		달러. 잘못 판정된 사람(VPN · 한국에 있는 외국인)이 여기서 바꾼다. 고른 값은 다음 방문에도 남는다.
		결제 통화는 바꾸지 않는다 — 그건 배송국이 정한다.
	*/
	const currency = useDisplayCurrency()

	return (
		<View css={style} className="siteFooter">
			<View className="mfInner">
				<View className="mfTop">
					{/*
						브랜드 스탬프 — 세로 조합(원형 위·텍스트 아래·좌우 중앙). 네비는 가로 조합이라
						형태가 겹치지 않는다. 링크 3열과 같은 높이에서 시작해 비대칭 균형을 만든다.
					*/}
					<View className="mfBrand">
						<View className="mfLogo">
							<Image src={logoPng} className="mfLogoSymbol" />
							<Image src={logoWordingPng} className="mfLogoWording" />
						</View>
						<span className="mfSignature">the way, the truth, and the life</span>
					</View>

					<View className="mfGrid">
						<View className="mfCol">
							<span className="mfHead">Products</span>
							<Pressable className="mfItem" onClick={() => navigate('/products/pls8')}>
								Koinos PLS 8
							</Pressable>
							<Pressable className="mfItem" onClick={() => navigate('/products/kdm6')}>
								Kedem 6
							</Pressable>
							<Pressable className="mfItem" onClick={() => navigate('/products/kdm4')}>
								Kedem 4
							</Pressable>
							<Pressable className="mfItem" onClick={() => navigate('/products/amp2')}>
								AirMIDI POG 2
							</Pressable>
							<Pressable className="mfItem" onClick={() => navigate('/products/ds')}>
								Dual Switch
							</Pressable>
						</View>

						<View className="mfCol">
							<span className="mfHead">Shop</span>
							<Pressable className="mfItem" onClick={() => navigate('/shop')}>
								Shop
							</Pressable>
							<Pressable className="mfItem" onClick={() => navigate('/cart')}>
								Cart
							</Pressable>
							<Pressable className="mfItem" onClick={() => navigate('/store-policy')}>
								Store Policy
							</Pressable>
							<Pressable className="mfItem" onClick={() => CookieConsent.reset()}>
								Cookie Settings
							</Pressable>
							<span className="mfItem mfCurrency" role="group" aria-label="Prices in">
								<button
									type="button"
									className={currency === 'USD' ? 'on' : ''}
									aria-pressed={currency === 'USD'}
									onClick={() => DisplayCurrency.set('USD')}
								>
									USD
								</button>
								{' · '}
								<button
									type="button"
									className={currency === 'KRW' ? 'on' : ''}
									aria-pressed={currency === 'KRW'}
									onClick={() => DisplayCurrency.set('KRW')}
								>
									KRW
								</button>
							</span>
						</View>

						<View className="mfCol">
							<span className="mfHead">Support</span>
							<Pressable className="mfItem" onClick={() => navigate('/manuals')}>
								Manuals
							</Pressable>
							<Pressable className="mfItem" onClick={() => navigate('/software')}>
								Software
							</Pressable>
							<Pressable className="mfItem" onClick={() => navigate('/works-with')}>
								Works with
							</Pressable>
							<Pressable className="mfItem" onClick={() => navigate('/faq')}>
								FAQ
							</Pressable>
							{/* 사이트 밖 — forum.judahs.io (FORUM_PLAN §14). 같은 탭으로 넘어간다.
							    href 는 크롤러 몫이고, 클릭은 로그인돼 있으면 로그인된 채로 착지시킨다 (§11) */}
							<a
								className="mfItem"
								href={FORUM_URL}
								onClick={e => {
									e.preventDefault()
									window.location.href = forumEntryUrl()
								}}
							>
								Forum
							</a>
						</View>

						<View className="mfCol">
							<span className="mfHead">Company</span>
							<Pressable className="mfItem" onClick={() => navigate('/about')}>
								About
							</Pressable>
							<Pressable className="mfItem" onClick={() => navigate('/contact')}>
								Contact
							</Pressable>
							<a className="mfItem" href="mailto:cs@judahs.io">
								cs@judahs.io
							</a>
							<Pressable className="mfItem" onClick={() => navigate('/privacy-policy')}>
								Privacy Policy
							</Pressable>
							<span className="mfItem mfSocial">
								<a
									href="https://www.youtube.com/@judahs-io"
									target="_blank"
									rel="noreferrer"
								>
									YouTube
								</a>
								{' · '}
								<a
									href="https://www.instagram.com/judahs_social"
									target="_blank"
									rel="noreferrer"
								>
									Instagram
								</a>
							</span>
						</View>
					</View>
				</View>

				<View className="mfLegal">
					{/*
						한국어 표기 한 줄 (2026-09-21, SEO_PLAN §18). 사이트 본문이 전부 영어라
						"주다스"·"코이노스"·"케뎀" 이 어느 페이지에도 없었고, 구글은 페이지에 없는
						문자열로 그 페이지를 띄우지 않는다. 문구 SSOT = seo.config 의 SEO_KO.
					*/}
					<span className="mfKo" lang="ko">
						{SEO_KO.footerLine}
					</span>
					<View className="mfLegalRow">
						<span>
							CEO: Boram Kim · Business No. 469-06-02816 · Unit 304, 1225 Gonghang-ro,
							Gangseo-gu, Busan, Rep. of Korea · Tel +82 51 791 1206
						</span>
						<span>{`© ${new Date().getFullYear()} Judahs · judahs.io`}</span>
					</View>
				</View>
			</View>
		</View>
	)
}

const style = css({
	width: '100%',
	// 구 페이지들은 본문 바깥 View 에 grey[900]/grey[300] 하단 선을 두고 있다. 페이지마다 지우는 대신
	// 푸터가 1px 위로 올라가 그 선을 자기 헤어라인으로 덮는다. 선이 없는 페이지에서는 아무 차이가 없다.
	position: 'relative',
	zIndex: 1,
	marginTop: -1,
	borderTop: `1px solid ${mono.line}`,
	background: mono.bg,
	fontFamily: monoFont.sans,
	textAlign: 'left',

	'.mfInner': {
		width: '100%',
		maxWidth: monoMaxWidth,
		margin: '0 auto',
		padding: `80px ${monoGutter} 40px ${monoGutter}`,
	},

	// 12열 기준 4 : 8 비대칭. 왼쪽은 스탬프, 오른쪽은 링크.
	'.mfTop': {
		display: 'grid',
		gridTemplateColumns: 'minmax(0, 4fr) minmax(0, 8fr)',
		gap: 48,
		alignItems: 'start',
	},

	'.mfGrid': {
		display: 'grid',
		gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
		gap: 40,
	},

	// 브랜드 스탬프: 세로 조합(원형 위·텍스트 아래·좌우 중앙). 블록 자체는 왼쪽에 붙고,
	// 블록 안에서 로고와 시그니처가 서로 중앙 정렬된다.
	'.mfBrand': {
		display: 'inline-flex',
		flexDirection: 'column',
		alignItems: 'center',
		textAlign: 'center',
		gap: 20,
		width: 'fit-content',
		// 키커(11px, line-height 1) 의 캡 높이와 로고 상단을 맞춘다
		marginTop: -6,

		'.mfLogo': {
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'center',
			gap: 14,

			'.mfLogoSymbol': {
				height: 52,
				width: 'auto',
				display: 'block',
			},
			'.mfLogoWording': {
				width: '5.25rem',
				height: 'auto',
				display: 'block',
			},
		},
		'.mfSignature': {
			fontSize: 12,
			fontWeight: 300,
			letterSpacing: '.02em',
			lineHeight: 1,
			color: mono.faint,
			whiteSpace: 'nowrap',
		},
	},

	'.mfCol': {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start',
		gap: 12,
	},

	'.mfHead': {
		fontSize: 11,
		letterSpacing: '.22em',
		textTransform: 'uppercase',
		color: mono.faint,
		fontWeight: 500,
	},

	'.mfItem': {
		display: 'block',
		padding: 0,
		fontFamily: monoFont.sans,
		fontSize: 13,
		fontWeight: 300,
		color: mono.dim,
		textDecoration: 'none',
		lineHeight: 1.3,
		transition: 'color 200ms ease-out',

		'&:hover': {
			color: mono.ink,
		},
		'&:focus-visible': {
			outline: `1px solid ${mono.ink}`,
			outlineOffset: 3,
		},

		// 구 페이지의 전역 a 스타일(밑줄 등)이 흘러들어오지 않게 여기서 고정한다.
		'&.mfSocial a': {
			color: mono.dim,
			textDecoration: 'none',
			'&:hover': { color: mono.ink },
		},

		// 통화 스위치 — 링크와 같은 글자, 고른 쪽만 `ink`. 버튼 외형은 없다.
		'&.mfCurrency': {
			color: mono.faint,
			'& button': {
				background: 'none',
				border: 0,
				padding: 0,
				font: 'inherit',
				color: mono.dim,
				cursor: 'pointer',
				transition: 'color 200ms ease-out',
				'&:hover': { color: mono.ink },
				'&.on': { color: mono.ink, fontWeight: 500 },
				'&:focus-visible': {
					outline: `1px solid ${mono.ink}`,
					outlineOffset: 3,
				},
			},
		},
	},

	'.mfLegal': {
		display: 'flex',
		flexDirection: 'column',
		gap: 10,
		marginTop: 64,
		paddingTop: 24,
		borderTop: `1px solid ${mono.line}`,
		fontSize: 11,
		fontWeight: 300,
		color: mono.faint,
		lineHeight: 1.5,

		'.mfLegalRow': {
			display: 'flex',
			justifyContent: 'space-between',
			gap: 24,
		},

		// 한글은 Archivo 에 글리프가 없어 어차피 시스템 폰트로 떨어진다. 그 폰트를 명시해
		// 브라우저마다 다른 폴백(굴림 등)이 잡히지 않게 한다.
		'.mfKo': {
			fontFamily: '"Apple SD Gothic Neo", "Noto Sans KR", "Malgun Gothic", sans-serif',
			letterSpacing: 0,
		},
	},

	'@media (max-width: 900px)': {
		'.mfTop': {
			gridTemplateColumns: '1fr',
			gap: 56,
		},
		'.mfGrid': {
			gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
			gap: 40,
		},
		'.mfBrand': {
			marginTop: 0,
		},
		'.mfLegal .mfLegalRow': {
			flexDirection: 'column',
			gap: 8,
		},
	},
})
